import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContentHighPriority, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput } from '@/components/ui/phone-input';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { validarCPF, formatarCPF } from '@/lib/validationUtils';

interface GestorCessionarioEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tipo: 'gestor' | 'cessionario';
  dadosIniciais?: any;
  onSave: (data: any) => Promise<void>;
}

export function GestorCessionarioEditDialog({
  open,
  onOpenChange,
  tipo,
  dadosIniciais,
  onSave,
}: GestorCessionarioEditDialogProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const hasInitialized = useRef(false);
  const [formData, setFormData] = useState({
    nome: '',
    telefone: '',
    whatsapp: '',
    email: '',
    rg: '',
    cpf: '',
  });

  useEffect(() => {
    // Resetar flag quando modal fecha
    if (!open) {
      hasInitialized.current = false;
      return;
    }
    
    // Só inicializar quando modal ABRE pela primeira vez
    if (open && !hasInitialized.current) {
      hasInitialized.current = true;
      
      if (dadosIniciais) {
        setFormData({
          nome: dadosIniciais.nome || dadosIniciais.nome_completo || '',
          telefone: dadosIniciais.telefone || '',
          whatsapp: dadosIniciais.whatsapp || '',
          email: dadosIniciais.email || '',
          rg: dadosIniciais.rg || '',
          cpf: dadosIniciais.cpf || '',
        });
      } else {
        setFormData({
          nome: '',
          telefone: '',
          whatsapp: '',
          email: '',
          rg: '',
          cpf: '',
        });
      }
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.nome.trim()) {
      toast({
        variant: 'destructive',
        title: 'Campo obrigatório',
        description: 'Por favor, informe o nome completo.',
      });
      return;
    }

    if (!formData.telefone.trim()) {
      toast({
        variant: 'destructive',
        title: 'Campo obrigatório',
        description: 'Por favor, informe o telefone.',
      });
      return;
    }

    if (!formData.whatsapp.trim()) {
      toast({
        variant: 'destructive',
        title: 'Campo obrigatório',
        description: 'Por favor, informe o WhatsApp.',
      });
      return;
    }

    if (!formData.email.trim()) {
      toast({
        variant: 'destructive',
        title: 'Campo obrigatório',
        description: 'Por favor, informe o e-mail.',
      });
      return;
    }

    if (!formData.rg.trim()) {
      toast({
        variant: 'destructive',
        title: 'Campo obrigatório',
        description: 'Por favor, informe o RG.',
      });
      return;
    }

    if (!formData.cpf.trim()) {
      toast({
        variant: 'destructive',
        title: 'Campo obrigatório',
        description: 'Por favor, informe o CPF.',
      });
      return;
    }

    // Validar CPF
    if (!validarCPF(formData.cpf)) {
      toast({
        variant: 'destructive',
        title: 'CPF inválido',
        description: 'Por favor, informe um CPF válido.',
      });
      return;
    }

    setIsLoading(true);
    try {
      console.log('🔵 Iniciando salvamento...', { tipo, formData });
      
      // Preparar dados (mesma estrutura para gestor e cessionário)
      const dataToSave = tipo === 'cessionario' 
        ? {
            nome_completo: formData.nome,
            telefone: formData.telefone,
            whatsapp: formData.whatsapp,
            email: formData.email,
            rg: formData.rg,
            cpf: formatarCPF(formData.cpf),
          }
        : {
            nome: formData.nome,
            telefone: formData.telefone,
            whatsapp: formData.whatsapp,
            email: formData.email,
            rg: formData.rg,
            cpf: formatarCPF(formData.cpf),
          };
      
      console.log('🔵 Dados preparados:', dataToSave);
      
      await onSave(dataToSave);
      
      console.log('✅ Salvamento concluído com sucesso!');
      await queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'cliente-resumo-360' });
      
      toast({
        title: 'Sucesso!',
        description: `${tipo === 'gestor' ? 'Gestor' : 'Cedente'} salvo com sucesso.`,
      });
      
      onOpenChange(false);
    } catch (error) {
      console.error('❌ Erro ao salvar:', error);
      
      toast({
        variant: 'destructive',
        title: 'Erro ao salvar',
        description: error instanceof Error ? error.message : 'Ocorreu um erro ao salvar os dados. Por favor, tente novamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentHighPriority className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {dadosIniciais ? 'Editar' : 'Adicionar'} {tipo === 'gestor' ? 'Gestor' : 'Cedente'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="nome">
              Nome Completo <span className="text-destructive">*</span>
            </Label>
            <Input
              id="nome"
              value={formData.nome}
              onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <PhoneInput
              label="Telefone *"
              value={formData.telefone}
              onChange={(value) => setFormData({ ...formData, telefone: value })}
              showOperator={true}
            />

            <PhoneInput
              label="WhatsApp *"
              value={formData.whatsapp}
              onChange={(value) => setFormData({ ...formData, whatsapp: value })}
              showOperator={true}
            />
          </div>

          <div>
            <Label htmlFor="email">
              E-mail <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cpf">
                CPF <span className="text-destructive">*</span>
              </Label>
              <Input
                id="cpf"
                value={formData.cpf}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  const formatted = value.length <= 11 
                    ? value.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
                    : formData.cpf;
                  setFormData({ ...formData, cpf: formatted });
                }}
                placeholder="000.000.000-00"
                maxLength={14}
                required
              />
            </div>

            <div>
              <Label htmlFor="rg">
                RG <span className="text-destructive">*</span>
              </Label>
              <Input
                id="rg"
                value={formData.rg}
                onChange={(e) => setFormData({ ...formData, rg: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar
            </Button>
          </div>
        </form>
      </DialogContentHighPriority>
    </Dialog>
  );
}
